import React, { useState, useEffect } from 'react';
import { Card, Typography, Alert, Empty } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { config } from '../config/env';
import { savingsReasonText } from './InsufficientDataCard';

const { Title, Text } = Typography;

/**
 * P1 M&V rewrite.
 *
 * The previous implementation INVENTED peer data: city average = user × 0.85,
 * state × 0.90, national × 0.80, plus hardcoded fallback metrics on error.
 * There is no aggregated peer dataset, so percentile claims were fiction.
 *
 * This component now shows only the user's own metered figure (when the
 * backend vouches for it) and states plainly that peer benchmarks don't exist.
 */
export const PeerComparison: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userValue, setUserValue] = useState<number | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    fetchPeerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchPeerData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.apiUrl}/api/energy/savings/metrics?days=30`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const payload = response.data.savings;
      if (payload?.status === 'ok' && payload.metrics) {
        setUserValue(payload.metrics.dollarsSaved ?? null);
        setReason(
          payload.metrics.dollarsSaved == null
            ? 'Dollar savings need a recorded location and a real tariff.'
            : null,
        );
      } else {
        setUserValue(null);
        setReason(savingsReasonText(payload));
      }
    } catch (error) {
      console.error('Failed to fetch savings data:', error);
      setUserValue(null);
      setReason('Savings service unreachable — no figures are shown.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  if (loading) {
    return <Card loading={loading} />;
  }

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <Title level={3}>How You Compare</Title>
        <Text type="secondary">Your metered savings, last 30 days</Text>
      </div>

      {userValue !== null ? (
        <Card style={{ background: '#f6ffed', textAlign: 'center' }}>
          <Text type="secondary" style={{ display: 'block' }}>
            Metered savings (30 days)
          </Text>
          <Title level={2} style={{ color: '#3f8600', margin: '8px 0 0' }}>
            {formatCurrency(userValue)}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Computed from metered records against a fitted pre-period baseline
          </Text>
        </Card>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={reason || 'No metered data available yet.'}
        />
      )}

      <Alert
        style={{ marginTop: '16px' }}
        message="Peer benchmarking not available"
        description="Percentile comparisons require an aggregated anonymized dataset, which does not exist yet — so no peer averages are shown, only your own metered figures."
        type="info"
        showIcon
      />
    </Card>
  );
};

export default PeerComparison;